import { type FocusEvent, type KeyboardEvent, type Ref, forwardRef, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatYmd } from "../../lib/format/datetime";

/**
 * A date field that reads and types DD/MM/YYYY in every language and
 * browser (user, 2026-10-31: every GMS date is DD/MM/YYYY). A native
 * `<input type="date">` shows the BROWSER's order — MM/DD/YYYY on a US
 * English Chrome — so it is never shown: it only backs the calendar button
 * (`showPicker`), and the text field beside it is what people read and type.
 *
 * The value on both sides is the wire's `YYYY-MM-DD` ("" = none):
 * `onChange` fires with a real calendar date once one is typed in full (or
 * picked), and with "" when the field is cleared. Half-typed text never
 * reaches the caller; on blur it goes back to the last good value.
 * `className` styles the frame (`input w-full`, `input input-sm`…), like a
 * daisyUI input with an icon inside.
 */
export interface DateInputProps {
	id?: string | undefined;
	value: string;
	onChange: (ymd: string) => void;
	min?: string | undefined;
	max?: string | undefined;
	className?: string | undefined;
	invalid?: boolean | undefined;
	disabled?: boolean | undefined;
	required?: boolean | undefined;
	"aria-label"?: string | undefined;
	"aria-describedby"?: string | undefined;
	onBlur?: ((e: FocusEvent<HTMLInputElement>) => void) | undefined;
	onKeyDown?: ((e: KeyboardEvent<HTMLInputElement>) => void) | undefined;
}

const ISO = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * "15/11/2026", "15-11-2026", "15.11.2026", "15112026" → "2026-11-15" when it
 * is a real day; else null. With `shortYear`, a two-digit year is this
 * century ("15/11/26") — only when the field is left: mid-typing, "15/11/20"
 * is on its way to 2026, not 2020.
 */
export function parseDmy(text: string, shortYear = false): string | null {
	const t = text.trim();
	const full = shortYear ? /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{4}|\d{2})$/ : /^(\d{1,2})[/.\-\s](\d{1,2})[/.\-\s](\d{4})$/;
	const m = full.exec(t) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(t);
	if (!m) return null;
	const [d, mo, y] = [Number(m[1]), Number(m[2]), m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3])];
	const at = new Date(Date.UTC(y, mo - 1, d));
	if (at.getUTCFullYear() !== y || at.getUTCMonth() !== mo - 1 || at.getUTCDate() !== d) return null;
	return `${y}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

const PART_MAX = [2, 2, 4] as const;

/**
 * The typed text as day / month / year (user, 2026-11-03: typing a date by
 * hand was buggy). Digits fill the day, then the month, then the year — a
 * full part moves on by itself, so the slashes appear as you type whether or
 * not some are there already ("15/112026" → "15/11/2026"); a separator
 * (/ . - or space) ends a part early ("1/" stays "1/"); anything else is dropped.
 */
export function masked(raw: string): string {
	const parts = [""];
	for (const ch of raw) {
		const i = parts.length - 1;
		if (/\d/.test(ch)) {
			if (parts[i]!.length < PART_MAX[i]!) parts[i] += ch;
			else if (i < 2) parts.push(ch);
		} else if (/[/.\-\s]/.test(ch) && parts[i] !== "" && i < 2) parts.push("");
	}
	return parts.join("/");
}

const shown = (ymd: string) => (ISO.test(ymd) ? formatYmd(ymd) : "");

export const DateInput = forwardRef(function DateInput(
	{ id, value, onChange, min, max, className = "input w-full", invalid, disabled, required, onBlur, onKeyDown, ...aria }: DateInputProps,
	ref: Ref<HTMLInputElement>,
) {
	const { t } = useTranslation("common");
	const [text, setText] = useState(() => shown(value));
	const editing = useRef(false);
	const picker = useRef<HTMLInputElement>(null);

	// A value set from outside (a reset, a looked-up flight) shows at once. The person's own typing is never
	// rewritten while the field has focus: padding "1" to "01" mid-edit pushed the next digit past the month.
	useEffect(() => {
		if (editing.current && (parseDmy(text) === value || (value === "" && text.trim() === ""))) return;
		setText(shown(value));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [value]);

	const type = (raw: string) => {
		editing.current = true;
		const next = masked(raw);
		setText(next);
		if (next.trim() === "") {
			if (value !== "") onChange("");
			return;
		}
		const ymd = parseDmy(next);
		if (ymd && ymd !== value) onChange(ymd);
	};

	const openPicker = () => {
		const el = picker.current;
		if (!el || disabled) return;
		try {
			el.showPicker();
		} catch {
			el.focus();
		}
	};

	return (
		<span className={`${className} ${invalid ? "input-error" : ""}`}>
			<input
				ref={ref}
				id={id}
				type="text"
				inputMode="numeric"
				autoComplete="off"
				placeholder={t("dateInput.placeholder")}
				value={text}
				disabled={disabled}
				required={required}
				aria-invalid={invalid || undefined}
				aria-label={aria["aria-label"]}
				aria-describedby={aria["aria-describedby"] ?? (invalid && id ? `${id}-error` : undefined)}
				className="min-w-0 grow tabular-nums"
				onChange={(e) => type(e.target.value)}
				onBlur={(e) => {
					editing.current = false;
					// Leaving the field shows the date in full ("1/2/26" → "01/02/2026"); half-typed text goes back to the last good value.
					const ymd = text.trim() === "" ? null : parseDmy(text, true);
					if (ymd && ymd !== value) onChange(ymd);
					if (text.trim() !== "") setText(shown(ymd ?? value));
					onBlur?.(e);
				}}
				onKeyDown={onKeyDown}
			/>
			<button
				type="button"
				tabIndex={-1}
				aria-label={t("dateInput.pick")}
				title={t("dateInput.pick")}
				disabled={disabled}
				onClick={openPicker}
				className="relative -mr-[0.25rem] grid size-[1.75rem] shrink-0 cursor-pointer place-items-center rounded-[0.375rem] text-gms-ink-3 hover:bg-gms-soft hover:text-gms-navy disabled:cursor-not-allowed"
			>
				<svg viewBox="0 0 24 24" aria-hidden className="size-[1rem]" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
					<rect x="3" y="4.5" width="18" height="16.5" rx="2" />
					<path d="M3 9.5h18M8 2.5v4M16 2.5v4" />
				</svg>
				<input
					ref={picker}
					type="date"
					tabIndex={-1}
					aria-hidden
					value={ISO.test(value) ? value : ""}
					min={min}
					max={max}
					onChange={(e) => {
						editing.current = false;
						setText(shown(e.target.value));
						onChange(e.target.value);
					}}
					className="pointer-events-none absolute inset-0 opacity-0"
				/>
			</button>
		</span>
	);
});
